import { useEffect, useMemo, useState } from 'react'
import { useApp } from '../state/AppStore'
import {
  detectBye,
  detectFixture,
  nextFixture,
  opponentLabel,
  pickableFixtures,
  relativeDayLabel,
  type ByeMatch,
  type FixtureMatch,
} from '../domain/fixture'
import { defaultPresent, purgeAbandonedFillIns, selectableForGame } from '../state/fillIns'
import { listDrafts } from '../state/drafts'
import { formatDate, todayISO } from '../lib/date'
import { newId } from '../lib/id'
import { isSampleData } from '../data/snapshotClient'
import type { SnapshotGame } from '../data/snapshotTypes'
import type { Game, HomeAway, ID } from '../domain/types'

/**
 * Pre-game setup.
 *
 * The fast path is meant to be: open the app, see tonight's game already
 * identified with the roster ticked, press Start. Everything else -- picking a
 * different fixture, adding a fill-in, setting up a game with no fixture at all
 * -- is available but deliberately out of the way.
 */
export function GameSetup({ onStarted }: { onStarted: (gameId: ID) => void }) {
  const {
    players,
    opponents,
    currentSeason,
    snapshot,
    snapshotError,
    snapshotRefreshing,
    refreshFromServer,
    repo,
    reload,
    games,
    currentGameId,
    deleteGame,
    addPlayer,
    updatePlayer,
    addOpponent,
    saveGame,
    setCurrentGameId,
  } = useApp()

  const today = todayISO()
  const fixtures = snapshot?.snapshot.games ?? []
  const byes = snapshot?.snapshot.byes ?? []

  const detected = useMemo(() => detectFixture(fixtures, today), [fixtures, today])
  // Asked separately: a bye is the absence of a game, so without this the next
  // real fixture would be presented as though it were tonight's.
  const bye = useMemo(() => (detected ? null : detectBye(byes, today)), [detected, byes, today])
  const upNext = useMemo(() => (detected ? null : nextFixture(fixtures, today)), [detected, fixtures, today])
  const suggestion = detected ?? upNext

  const [selectedGameId, setSelectedGameId] = useState<string | null>(null)
  const [showPicker, setShowPicker] = useState(false)
  const [manualMode, setManualMode] = useState(false)
  const [present, setPresent] = useState<Set<ID>>(new Set())
  const [newPlayer, setNewPlayer] = useState('')
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)

  // Manual-mode fields, only used when there is no fixture to work from.
  const [manualOpponentId, setManualOpponentId] = useState<ID | null>(null)
  const [manualDate, setManualDate] = useState(today)
  const [manualHomeAway, setManualHomeAway] = useState<HomeAway>('home')
  const [newOpponent, setNewOpponent] = useState('')

  const selected: SnapshotGame | null = useMemo(() => {
    const id = selectedGameId ?? suggestion?.game.playhq_game_id ?? null
    return id ? (fixtures.find((g) => g.playhq_game_id === id) ?? null) : null
  }, [selectedGameId, suggestion, fixtures])

  // Fill-ins belong to one game. Pending ones (added during a setup that was
  // never started) are cleaned up on arrival, which also clears any residue.
  useEffect(() => {
    void purgeAbandonedFillIns(repo, players).then((kept) => {
      if (kept.length !== players.length) void reload()
    })
    // Only on mount: this is a cleanup of the previous session, not a live rule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectablePlayers = useMemo(() => selectableForGame(players, null), [players])

  // Games left in progress via "Keep as draft". Offered here rather than resumed
  // automatically -- the scorer chose to step away from them.
  const drafts = useMemo(() => listDrafts(games, currentGameId), [games, currentGameId])

  // Pre-tick the synced squad once it is known. Only seeds an empty selection, so
  // it never fights the scorer after they start un-ticking absentees. Fill-ins
  // are never pre-ticked -- they are not squad members.
  useEffect(() => {
    if (present.size > 0) return
    const squad = defaultPresent(players)
    if (squad.length > 0) setPresent(new Set(squad.map((p) => p.id)))
  }, [players, present.size])

  const togglePresent = (id: ID) => {
    setPresent((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleAddPlayer = async () => {
    const name = newPlayer.trim()
    if (!name) return
    const player = await addPlayer(name)
    // Not in the synced roster, so flag as a fill-in for the admin to resolve.
    // fillInForGameId stays null until Start, which scopes them to this game.
    // Uses the returned record, not a lookup -- `players` state is still stale here.
    await updatePlayer({ ...player, isFillIn: true, fillInForGameId: null })
    setNewPlayer('')
    setPresent((prev) => new Set(prev).add(player.id))
  }

  const handleAddOpponent = async () => {
    const name = newOpponent.trim()
    if (!name) return
    const opponent = await addOpponent(name)
    setNewOpponent('')
    setManualOpponentId(opponent.id)
  }

  const canStart = present.size > 0 && !starting && (manualMode ? manualOpponentId != null : selected != null)

  const start = async () => {
    if (!canStart) return

    /*
     * A fixture game now carries the server's id, so starting the same fixture
     * twice lands on the same record rather than making a second one. That is
     * what we want -- one fixture, one game -- but it means an already finished
     * game must not be quietly reopened and overwritten.
     */
    const existing = !manualMode && selected ? games.find((g) => g.id === selected.id) : undefined
    if (existing && existing.status === 'final') {
      setStartError(
        'That game has already been finished on this device. Open it from Stats to correct it, or discard it in Manage first.',
      )
      return
    }

    setStarting(true)
    setStartError(null)
    const now = Date.now()

    const base = {
      seasonId: currentSeason.id,
      roster: [...present],
      ourScore: null,
      theirScore: null,
      status: 'in_progress' as const,
      currentPeriod: 1,
      notes: '',
      createdAt: now,
      updatedAt: now,
    }

    const game: Game =
      manualMode || !selected
        ? {
            ...base,
            // No fixture, so no server row to share an id with. This game stays
            // on this device; the live view and take-over do not apply to it.
            id: newId(),
            date: manualDate,
            playhqGameId: null,
            opponentId: manualOpponentId,
            opponentName: opponents.find((o) => o.id === manualOpponentId)?.name ?? 'Opponent',
            homeAway: manualHomeAway,
          }
        : {
            ...base,
            /*
             * The SERVER's games.id, not a fresh one.
             *
             * Same reasoning as 03.1(2) for players, and the same cost if it is
             * skipped: two devices scoring this fixture would each invent their
             * own id, their events would point at different games, and the live
             * tally would quietly show half the taps. Nothing would error.
             */
            id: selected.id,
            date: selected.date,
            // The merge key: results arriving by sync attach to this game.
            playhqGameId: selected.playhq_game_id,
            opponentId: null,
            opponentName: selected.opponent_name ?? 'Opponent',
            homeAway: selected.home_away,
            roundName: selected.round_name,
            roundAbbreviation: selected.round_abbreviation,
            venueName: selected.venue_name,
            courtName: selected.court_name,
            isFinalRound: selected.is_final_round,
            countsTowardLadder: selected.counts_toward_ladder,
          }

    // Resuming a draft of the same fixture: keep what was recorded, take the
    // roster the scorer just ticked.
    await saveGame(existing ? { ...existing, roster: game.roster, updatedAt: now } : game)
    // Bind any fill-ins added during setup to this game, so they never surface
    // on another one.
    for (const id of present) {
      const p = players.find((x) => x.id === id)
      if (p?.isFillIn && (p.fillInForGameId ?? null) === null) {
        await updatePlayer({ ...p, fillInForGameId: game.id })
      }
    }
    await setCurrentGameId(game.id)
    onStarted(game.id)
  }

  const rosterKnown = selectablePlayers.some((p) => p.playhqProfileId)

  return (
    <div className="setup">
      {drafts.length > 0 && (
        <section className="panel">
          <h2 className="panel__title">Unfinished games</h2>
          <div className="drafts">
            {drafts.map((d) => (
              <div key={d.id} className="draft">
                <div className="draft__label">
                  <div className="draft__title">
                    {d.roundAbbreviation ? `${d.roundAbbreviation} · ` : ''}
                    {d.homeAway === 'home' ? 'vs' : '@'} {d.opponentName ?? 'Opponent'}
                  </div>
                  <div className="draft__meta">{formatDate(d.date)}</div>
                </div>
                <button type="button" className="btn btn--sm" onClick={() => void setCurrentGameId(d.id).then(onStarted.bind(null, d.id))}>
                  Resume
                </button>
                <button type="button" className="btn btn--sm btn--danger" onClick={() => void deleteGame(d)}>
                  Discard
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="setup__cols">
        <section className="panel">
          {!manualMode && bye ? (
            <ByeCard
              bye={bye}
              next={upNext}
              onChange={() => setShowPicker((v) => !v)}
              pickerOpen={showPicker}
            />
          ) : !manualMode && suggestion && selected ? (
            <FixtureCard
              match={{ ...suggestion, game: selected }}
              isAutoDetected={!!detected && selected.playhq_game_id === detected.game.playhq_game_id}
              onChange={() => setShowPicker((v) => !v)}
              pickerOpen={showPicker}
            />
          ) : (
            <ManualSetup
              opponents={opponents}
              opponentId={manualOpponentId}
              onOpponent={setManualOpponentId}
              date={manualDate}
              onDate={setManualDate}
              homeAway={manualHomeAway}
              onHomeAway={setManualHomeAway}
              newOpponent={newOpponent}
              onNewOpponent={setNewOpponent}
              onAddOpponent={handleAddOpponent}
              canReturnToFixtures={fixtures.length > 0}
              onBackToFixtures={() => setManualMode(false)}
            />
          )}

          {showPicker && !manualMode && (
            <FixturePicker
              fixtures={pickableFixtures(fixtures, today)}
              selectedId={selected?.playhq_game_id ?? null}
              onPick={(id) => {
                setSelectedGameId(id)
                setShowPicker(false)
              }}
              onManual={() => {
                setManualMode(true)
                setShowPicker(false)
              }}
            />
          )}
        </section>

        <section className="panel">
          <h2 className="panel__title">
            Players present <span className="count-badge">{present.size}</span>
            {rosterKnown && <span className="panel__hint">from PlayHQ line-ups</span>}
          </h2>
          <div className="chip-grid chip-grid--players">
            {selectablePlayers.map((p) => (
              <button
                key={p.id}
                type="button"
                className={'chip chip--player' + (present.has(p.id) ? ' chip--on' : '')}
                onClick={() => togglePresent(p.id)}
              >
                {p.name}
                {p.isFillIn && <span className="chip__tag">fill-in</span>}
              </button>
            ))}
            {selectablePlayers.length === 0 && <p className="muted">No players yet. Add your squad below.</p>}
          </div>
          <div className="inline-add">
            <input
              value={newPlayer}
              onChange={(e) => setNewPlayer(e.target.value)}
              placeholder="Fill-in player"
              autoCapitalize="words"
              autoCorrect="off"
            />
            <button type="button" className="btn" onClick={handleAddPlayer}>
              Add
            </button>
          </div>
          <p className="muted small">Anyone added here is flagged as a fill-in for the admin to resolve.</p>
        </section>
      </div>

      {startError && <div className="banner banner--error">{startError}</div>}

      <button type="button" className="btn btn--primary btn--start" disabled={!canStart} onClick={start}>
        {canStart ? startLabel(selected, manualMode) : 'Pick a game and at least one player'}
      </button>

      {/* Housekeeping, not a feature: kept to one quiet line under the fold. */}
      <SnapshotBanner
        hasSnapshot={!!snapshot}
        error={snapshotError}
        refreshing={snapshotRefreshing}
        fetchedAt={snapshot?.fetchedAt ?? null}
        onRefresh={() => void refreshFromServer()}
      />
    </div>
  )
}

function startLabel(selected: SnapshotGame | null, manualMode: boolean): string {
  if (manualMode || !selected) return 'Start game'
  return `Start ${opponentLabel(selected)}`
}

/**
 * Shown when PlayHQ says we sit this round out. Without it the screen would
 * quietly offer the next real fixture, which is how "R19 @ Beam Team" appeared
 * on a night we had a bye.
 */
function ByeCard({
  bye,
  next,
  onChange,
  pickerOpen,
}: {
  bye: ByeMatch
  next: FixtureMatch | null
  onChange(): void
  pickerOpen: boolean
}) {
  return (
    <div className="fixture fixture--bye">
      <div className="fixture__when">
        <span className="fixture__rel">{relativeDayLabel(bye.dayOffset)}</span>
        <span className="fixture__date">{bye.bye.round_name ?? bye.bye.round_abbreviation}</span>
      </div>
      <div className="fixture__opponent">Bye, no game</div>
      {next ? (
        <p className="fixture__venue">
          Next game {next.game.round_abbreviation ?? ''} {opponentLabel(next.game)} ·{' '}
          {formatDate(next.game.date)}
          {next.game.start_time ? ` · ${next.game.start_time.slice(0, 5)}` : ''}
        </p>
      ) : (
        <p className="fixture__venue">No further games scheduled.</p>
      )}
      <p className="muted small">Nothing to score tonight. Pick a game below if that is wrong.</p>
      <button type="button" className="btn btn--sm fixture__change" onClick={onChange}>
        {pickerOpen ? 'Close' : 'Pick a game anyway'}
      </button>
    </div>
  )
}

function FixtureCard({
  match,
  isAutoDetected,
  onChange,
  pickerOpen,
}: {
  match: FixtureMatch
  isAutoDetected: boolean
  onChange(): void
  pickerOpen: boolean
}) {
  const { game, dayOffset } = match
  return (
    <div className={'fixture' + (isAutoDetected ? ' fixture--tonight' : '')}>
      <div className="fixture__when">
        <span className="fixture__rel">{relativeDayLabel(dayOffset)}</span>
        <span className="fixture__date">
          {formatDate(game.date)}
          {game.start_time ? ` · ${game.start_time.slice(0, 5)}` : ''}
        </span>
      </div>

      <div className="fixture__opponent">{opponentLabel(game)}</div>

      <div className="fixture__meta">
        {game.round_name && <span className="tag">{game.round_name}</span>}
        {game.is_final_round && <span className="tag tag--final">Finals</span>}
        {!game.counts_toward_ladder && !game.is_final_round && (
          <span className="tag tag--grading" title="Played, but excluded from the PlayHQ ladder">
            Grading round
          </span>
        )}
      </div>

      {game.venue_name && (
        <div className="fixture__venue">
          {game.venue_name}
          {game.court_name ? ` · ${game.court_name}` : ''}
        </div>
      )}

      {game.opponent_provisional && (
        <p className="muted small">Opponent not decided yet. PlayHQ still lists a placeholder.</p>
      )}

      <button type="button" className="btn btn--sm fixture__change" onClick={onChange}>
        {pickerOpen ? 'Close' : 'Different game'}
      </button>
    </div>
  )
}

function FixturePicker({
  fixtures,
  selectedId,
  onPick,
  onManual,
}: {
  fixtures: FixtureMatch[]
  selectedId: string | null
  onPick(id: string): void
  onManual(): void
}) {
  return (
    <div className="picker">
      {fixtures.map(({ game, dayOffset }) => (
        <button
          key={game.playhq_game_id}
          type="button"
          className={'picker__row' + (game.playhq_game_id === selectedId ? ' picker__row--on' : '')}
          onClick={() => onPick(game.playhq_game_id)}
        >
          <span className="picker__round">{game.round_abbreviation ?? '-'}</span>
          <span className="picker__date">{formatDate(game.date)}</span>
          <span className="picker__opp">{opponentLabel(game)}</span>
          <span className="picker__status">
            {game.status === 'FINAL' ? `${game.our_score}-${game.their_score} ${game.result ?? ''}` : relativeDayLabel(dayOffset)}
          </span>
        </button>
      ))}
      <button type="button" className="btn btn--sm picker__manual" onClick={onManual}>
        No fixture, set up manually
      </button>
    </div>
  )
}

function ManualSetup(props: {
  opponents: { id: ID; name: string }[]
  opponentId: ID | null
  onOpponent(id: ID): void
  date: string
  onDate(d: string): void
  homeAway: HomeAway
  onHomeAway(h: HomeAway): void
  newOpponent: string
  onNewOpponent(v: string): void
  onAddOpponent(): void
  canReturnToFixtures: boolean
  onBackToFixtures(): void
}) {
  return (
    <>
      <h2 className="panel__title">
        Manual game
        {props.canReturnToFixtures && (
          <button type="button" className="btn btn--sm panel__action" onClick={props.onBackToFixtures}>
            Use fixture
          </button>
        )}
      </h2>
      <div className="chip-grid">
        {props.opponents.map((o) => (
          <button
            key={o.id}
            type="button"
            className={'chip' + (o.id === props.opponentId ? ' chip--on' : '')}
            onClick={() => props.onOpponent(o.id)}
          >
            {o.name}
          </button>
        ))}
        {props.opponents.length === 0 && <p className="muted">No opponents yet. Add one below.</p>}
      </div>
      <div className="inline-add">
        <input
          value={props.newOpponent}
          onChange={(e) => props.onNewOpponent(e.target.value)}
          placeholder="New opponent"
          autoCapitalize="words"
          autoCorrect="off"
        />
        <button type="button" className="btn" onClick={props.onAddOpponent}>
          Add
        </button>
      </div>
      <div className="field-row">
        <label className="field">
          <span>Date</span>
          <input type="date" value={props.date} onChange={(e) => props.onDate(e.target.value)} />
        </label>
        <div className="field">
          <span>Venue</span>
          <div className="seg">
            <button
              type="button"
              className={'seg__btn' + (props.homeAway === 'home' ? ' seg__btn--on' : '')}
              onClick={() => props.onHomeAway('home')}
            >
              Home
            </button>
            <button
              type="button"
              className={'seg__btn' + (props.homeAway === 'away' ? ' seg__btn--on' : '')}
              onClick={() => props.onHomeAway('away')}
            >
              Away
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

function SnapshotBanner({
  hasSnapshot,
  error,
  refreshing,
  fetchedAt,
  onRefresh,
}: {
  hasSnapshot: boolean
  error: string | null
  refreshing: boolean
  fetchedAt: number | null
  onRefresh(): void
}) {
  const sample = isSampleData()
  if (!hasSnapshot && !error) return null

  const age = fetchedAt ? Math.round((Date.now() - fetchedAt) / 60000) : null
  const ageLabel = age === null ? '' : age < 1 ? 'just now' : age < 60 ? `${age}m ago` : `${Math.round(age / 60)}h ago`

  return (
    <div className={'syncfoot' + (error ? ' syncfoot--warn' : '')}>
      <span>
        {sample && <strong>Sample fixture data · </strong>}
        {error ? `${error} · showing fixtures saved ${ageLabel}` : `Fixtures synced ${ageLabel}`}
      </span>
      <button type="button" className="syncfoot__refresh" disabled={refreshing} onClick={onRefresh}>
        {refreshing ? 'Refreshing...' : 'Refresh'}
      </button>
    </div>
  )
}
